/**
 * Preview OAuth Utilities
 *
 * Handles OAuth redirects for preview deployments by storing the preview URL
 * in a cookie that can be accessed across subdomains.
 *
 * Flow:
 * 1. User visits preview deployment (e.g., branch-1.preview.terac.com)
 * 2. Preview URL is saved to a cookie with domain=.terac.com (accessible across subdomains)
 * 3. OAuth redirects to staging/production (configured in Google Console)
 * 4. Hook reads cookie and redirects back to preview
 */

export const PREVIEW_URL_COOKIE_NAME = "terac_preview_oauth_url";
export const PREVIEW_ORIGIN_COOKIE_NAME = "terac_preview_oauth_origin";

/**
 * Check if current hostname is a preview deployment
 * Preview deployments match patterns like:
 * - *.preview.terac.com
 * - *.vercel.app (Vercel preview)
 * - localhost (for local dev)
 */
export function isPreviewDeployment(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;

  // Local development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return false; // Don't treat localhost as preview for OAuth
  }

  // Preview subdomain pattern (e.g., branch-1.preview.terac.com)
  if (hostname.includes(".preview.")) {
    return true;
  }

  // Vercel preview deployments
  if (hostname.includes(".vercel.app")) {
    return true;
  }

  return false;
}

/**
 * Get cookie domain for cross-subdomain access
 * Returns ".terac.com" for terac.com subdomains, or null for localhost
 */
function getCookieDomain(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const hostname = window.location.hostname;

  // Local development - no domain restriction
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return null;
  }

  // For terac.com subdomains, use .terac.com to allow cross-subdomain access
  if (hostname.includes(".terac.com") || hostname === "terac.com") {
    return ".terac.com";
  }

  // For other domains, don't set domain (cookie only accessible on exact domain)
  return null;
}

/**
 * Save preview URL to cookie for OAuth redirect
 * Cookie is set with domain=.terac.com to allow access across subdomains
 */
export function savePreviewUrlToCookie(): void {
  if (typeof window === "undefined") {
    return;
  }

  if (!isPreviewDeployment()) {
    return;
  }

  const previewUrl = window.location.href;
  const previewOrigin = window.location.origin;
  const cookieDomain = getCookieDomain();

  try {
    // Set cookie with 10 minute expiration (same as OAuth state)
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + 10);

    const cookieOptions: string[] = [
      `${PREVIEW_URL_COOKIE_NAME}=${encodeURIComponent(previewUrl)}`,
      `expires=${expires.toUTCString()}`,
      "path=/",
      "SameSite=Lax",
    ];

    if (cookieDomain) {
      cookieOptions.push(`domain=${cookieDomain}`);
    }

    document.cookie = cookieOptions.join("; ");

    const originCookieOptions: string[] = [
      `${PREVIEW_ORIGIN_COOKIE_NAME}=${encodeURIComponent(previewOrigin)}`,
      `expires=${expires.toUTCString()}`,
      "path=/",
      "SameSite=Lax",
    ];

    if (cookieDomain) {
      originCookieOptions.push(`domain=${cookieDomain}`);
    }

    document.cookie = originCookieOptions.join("; ");

    console.log("💾 Saved preview URL to cookie for OAuth redirect:", {
      previewUrl,
      previewOrigin,
      cookieDomain,
    });
  } catch (error) {
    console.error("Failed to save preview URL to cookie:", error);
  }
}

/**
 * Clear preview URL cookies
 */
export function clearPreviewUrlCookies(): void {
  if (typeof window === "undefined") {
    return;
  }

  const cookieDomain = getCookieDomain();

  try {
    const cookieOptions: string[] = [
      `${PREVIEW_URL_COOKIE_NAME}=`,
      "expires=Thu, 01 Jan 1970 00:00:00 GMT",
      "path=/",
    ];

    if (cookieDomain) {
      cookieOptions.push(`domain=${cookieDomain}`);
    }

    document.cookie = cookieOptions.join("; ");

    const originCookieOptions: string[] = [
      `${PREVIEW_ORIGIN_COOKIE_NAME}=`,
      "expires=Thu, 01 Jan 1970 00:00:00 GMT",
      "path=/",
    ];

    if (cookieDomain) {
      originCookieOptions.push(`domain=${cookieDomain}`);
    }

    document.cookie = originCookieOptions.join("; ");

    console.log("🗑️ Cleared preview URL cookies");
  } catch (error) {
    console.error("Failed to clear preview URL cookies:", error);
  }
}
